/**
 * Geometry undo / redo / revert for the mask editor — the bridge between the editor's live state
 * and its history store.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-5 / AUD-2 RC-4) with no change to a single body, and
 * pointed at a PER-EDITOR store (`createEditorHistoryStore()`) instead of the app-wide singleton
 * (MN-5). The singleton export survives, so every existing reader — including
 * `stores/editorStore.test.ts` — is untouched.
 *
 * Four mechanisms travelled here verbatim. They are AUD-2's "undo/redo across a removal boundary"
 * healthy item, and one of them (`restoreRemovalRef` → `rebaseRemoval`) is the ED-1 data-loss
 * postmortem:
 *  - `pendingHistoryTargetRef`'s quiet window (ED-9) — an undo that crosses a removal boundary
 *    converges over SEVERAL renders, and every intermediate render used to commit a hybrid
 *    snapshot that truncated the redo tail;
 *  - `undoneMatteRef` / `undoneMatteKindRef` stashing (ED-8) — the re-apply direction needs the
 *    matte the restore dropped;
 *  - `canApplySnapshot`'s refusal to step past an unreplayable removal;
 *  - `restoreRemovalRef` → `rebaseRemoval` (ED-1) — a reopen restore re-establishes a SAVED state
 *    and must not be recorded as an undoable edit.
 * Drag ticks stay excluded from commits.
 */
import { useEffect, useRef } from "react";
import type { Mask, Polygon } from "@genz/engine-bind";
import {
  createEditorHistoryStore,
  canUndo as histCanUndo,
  canRedo as histCanRedo,
  currentSnapshot,
  snapshotKey,
  snapshotOf,
  type EditorSnapshot,
} from "../stores/editorStore.js";
import type { AppLogger } from "../app/context.js";
import type { MatteKind } from "./MaskEditor.js";

export interface EditorHistoryBridgeArgs {
  printArea: Polygon;
  cutouts: Mask[];
  brandArea?: Polygon | null;
  /** Whether the background removal is currently baked onto the source. */
  removed: boolean;
  /** Non-null while a handle drag is in flight — commits are skipped for the duration. */
  dragging: boolean;
  locked?: boolean;
  /** Re-seeds the history (as-opened = index 0) whenever it changes. */
  caseImageUrl?: string;
  /** The geometric view being authored (B4). A CHANGE re-seeds the history (F-35). */
  view?: string;
  onPrintAreaChange: (p: Polygon) => void;
  onCutoutsChange: (c: Mask[]) => void;
  onBrandAreaChange?: (p: Polygon) => void;
  /** The live matte plane (`EditorRefs.matteAlphaRef`) — one of the two matte sources a re-apply
   *  can replay; the other is `undoneMatteRef`, stashed by `resetBackground`. */
  matteAlphaRef: React.RefObject<Uint8ClampedArray | Uint8Array | null>;
  setPlacementMsg: (m: string | null) => void;
  logger?: AppLogger;
}

export function useEditorHistoryBridge(args: EditorHistoryBridgeArgs) {
  const {
    printArea,
    cutouts,
    brandArea,
    removed,
    dragging,
    locked,
    caseImageUrl,
    view,
    onPrintAreaChange,
    onCutoutsChange,
    onBrandAreaChange,
    matteAlphaRef,
    setPlacementMsg,
    logger,
  } = args;

  // --- Geometry undo / redo / reset via the editor history store (spec 019 US4).
  // History is REAL STATE (reliable button enablement, unlike the old refs).
  // Snapshots are committed on DISCRETE edits — skipped while a drag is active so
  // per-tick vertex moves don't flood history, then recorded once when the drag ends
  // (`drag` returns to null). Undo/redo echoes equal the current snapshot, so the
  // commit is a no-op. Reset restores the full as-opened snapshot, including a chroma
  // background removal. See stores/editorStore.ts.
  // MN-5: this store belongs to THIS editor. The app-wide singleton still exists for its other
  // readers, but it is no longer what the editor's undo runs on.
  const storeRef = useRef(createEditorHistoryStore());
  const useHistory = storeRef.current;
  const history = useHistory();
  const snapshot = (): EditorSnapshot =>
    snapshotOf({ printArea, cutouts, brandArea, backgroundRemoved: removed });
  /**
   * MN-11 / J-13 — the geometry AS OF THIS RENDER, for the seed effect below.
   *
   * The seed effect deliberately runs on a new case image and on NOTHING else: re-seeding when the
   * print area changes would reset the undo stack on every edit. Listing `printArea` / `cutouts` /
   * `brandArea` as deps would therefore be wrong, and omitting them was what the suppression hid.
   * A ref says the true thing — "the effect wants these values at seed time, not as triggers".
   */
  const latestGeoRef = useRef({ printArea, cutouts, brandArea });
  latestGeoRef.current = { printArea, cutouts, brandArea };
  /**
   * `removed` AS OF THIS RENDER, for the view effect below — read, never a trigger. Listing
   * `removed` as a dep of that effect would re-seed the history on every removal, which is the
   * opposite of what it is for.
   */
  const latestRemovedRef = useRef(removed);
  latestRemovedRef.current = removed;
  /** Late-bound ref to resetBackground so reset (defined here) can call it. */
  const resetBackgroundRef = useRef<(() => void) | null>(null);
  /**
   * ED-1 — the reopen matte RESTORE flipped `removed`, and the commit effect below recorded it as
   * an ordinary edit. Set right before the restore keys the matte so the effect RE-BASELINES the
   * history instead of committing a (falsely undoable) entry. Consumed exactly once.
   */
  const restoreRemovalRef = useRef(false);
  /**
   * ED-9 — the snapshot key an in-flight undo/redo/revert is driving toward, or null.
   *
   * `applySnapshot` can only reach the target over SEVERAL renders when it has to cross a
   * background-removal boundary: `resetBackground` reloads the original image ASYNCHRONOUSLY, so
   * the geometry lands one commit before `removed` does. Every one of those intermediate renders
   * used to `commit` a hybrid snapshot — which pushed a NEW entry and TRUNCATED the redo tail, so
   * Redo went dead right after an Undo. While this is set the commit effect stays quiet, and it
   * clears itself the moment the live snapshot equals the target (or when the transition fails).
   */
  const pendingHistoryTargetRef = useRef<string | null>(null);
  /**
   * The matte `resetBackground` dropped, kept so the OPPOSITE history direction (redo a removal /
   * undo a restore) can replay it — `matteAlphaRef` is intentionally cleared by the restore, which
   * left `applySnapshot` unable to re-apply and silently no-op'ing (ED-8).
   */
  const undoneMatteRef = useRef<Uint8ClampedArray | Uint8Array | null>(null);
  /** The TECHNIQUE of the matte `resetBackground` dropped, stashed alongside `undoneMatteRef` —
   *  the re-apply direction has to know whether to re-run the chroma key or replay the alpha. */
  const undoneMatteKindRef = useRef<MatteKind | null>(null);
  /** Late-bound ref to `applyMatteToSource` (defined far below) so the history helpers here can
   *  replay a matte. Returns true when the re-key actually landed on the canvas. */
  const applyMatteToSourceRef = useRef<((alpha: Uint8Array | Uint8ClampedArray) => boolean) | null>(null);

  // Seed history when a new case image loads (as-opened = index 0).
  useEffect(() => {
    const geo = latestGeoRef.current;
    useHistory.getState().init({
      printArea: geo.printArea,
      cutouts: geo.cutouts,
      brandArea: geo.brandArea ?? null,
      backgroundRemoved: false,
    });
    // A fresh image invalidates any in-flight history transition + the stashed matte.
    pendingHistoryTargetRef.current = null;
    restoreRemovalRef.current = false;
    undoneMatteRef.current = null;
    undoneMatteKindRef.current = null;
  }, [caseImageUrl, useHistory]);

  /**
   * F-35 / J-14 — RE-SEED on a view change, rather than being remounted into existence.
   *
   * A view switch used to destroy this hook and build a new one, which seeded the new view's
   * geometry as index 0. Undo must not step from the new view's masks back to the old view's, so
   * the history is re-seeded — the same `init` the mount performs — rather than carried over. The
   * first render is skipped: the seed effect above has already done exactly this.
   *
   * review-1 I-1 — the re-seed and the RESET OF `removed` do not land on the same render.
   * `useMatte.resetForView`'s `setRemoved(false)` is state, so it arrives one render after this
   * effect, while the commit effect below (deps include `removed`) runs on BOTH renders: it pushed
   * `{newGeo, removed:true}` and then `{newGeo, removed:false}` on top of the fresh seed, leaving
   * depth 3 / index 2 with Undo and Reset enabled on a target that names a removal whose matte this
   * very switch threw away — `canApplySnapshot` then refuses the step and the buttons are dead.
   * ED-9's quiet window is exactly the mechanism for "a transition that converges over several
   * renders", so arm it on the snapshot the reset converges to and both transitional commits are
   * swallowed (a remount used to give depth 1; so does this).
   *
   * ONLY when `removed` is true: a pending target nothing will ever satisfy would silently drop the
   * next real edit.
   */
  const seenHistoryViewRef = useRef<string | undefined>(view);
  useEffect(() => {
    if (seenHistoryViewRef.current === view) return;
    seenHistoryViewRef.current = view;
    const geo = latestGeoRef.current;
    const seed = {
      printArea: geo.printArea,
      cutouts: geo.cutouts,
      brandArea: geo.brandArea ?? null,
      backgroundRemoved: false,
    };
    useHistory.getState().init(seed);
    pendingHistoryTargetRef.current = latestRemovedRef.current
      ? snapshotKey(snapshotOf(seed))
      : null;
    restoreRemovalRef.current = false;
    undoneMatteRef.current = null;
    undoneMatteKindRef.current = null;
  }, [view, useHistory]);

  // Commit discrete edits; skip while dragging (commit once when `drag` clears).
  useEffect(() => {
    if (dragging) return;
    // J-13: built from THIS effect's own deps (see `snapshotOf`), so the list below is complete.
    const snap = snapshotOf({ printArea, cutouts, brandArea, backgroundRemoved: removed });
    // ED-9: an undo/redo/revert is mid-flight — see `pendingHistoryTargetRef`.
    const pending = pendingHistoryTargetRef.current;
    if (pending !== null) {
      if (snapshotKey(snap) === pending) pendingHistoryTargetRef.current = null;
      return;
    }
    // ED-1: the reopen restore re-established a SAVED state, not a user edit.
    if (restoreRemovalRef.current) {
      restoreRemovalRef.current = false;
      useHistory.getState().rebaseRemoval(snap.backgroundRemoved);
      return;
    }
    useHistory.getState().commit(snap);
  }, [printArea, cutouts, brandArea, removed, dragging, useHistory]);

  /** Whether the removal direction of `s` can actually be reached from the live state. Crossing
   *  INTO "removed" needs a matte to replay (the live one, or the one a restore stashed); the
   *  other direction always can (the original image is re-loadable). */
  const canApplySnapshot = (s: EditorSnapshot): boolean =>
    removed || !s.backgroundRemoved || !!(matteAlphaRef.current ?? undoneMatteRef.current);

  const applySnapshot = (s: EditorSnapshot) => {
    const crossesRemoval = removed !== !!s.backgroundRemoved;
    // Only arm the quiet window when the transition is genuinely multi-render (the async
    // image reload / re-key). A geometry-only step converges in one commit that equals the
    // history entry, so `commit` is already a no-op there.
    pendingHistoryTargetRef.current = crossesRemoval ? snapshotKey(s) : null;
    onPrintAreaChange(s.printArea);
    onCutoutsChange(s.cutouts);
    if (onBrandAreaChange && s.brandArea) onBrandAreaChange(s.brandArea);
    if (!crossesRemoval) return;
    if (removed) {
      // Truly revert a background removal so reset/undo return to the original image.
      resetBackgroundRef.current?.();
      return;
    }
    // ED-8: the RE-APPLY direction (redo a removal, or undo a "Restore background") used to be
    // unhandled, so the history said "removed" while the canvas showed the original.
    const alpha = matteAlphaRef.current ?? undoneMatteRef.current;
    if (alpha && applyMatteToSourceRef.current?.(alpha) === true) return;
    // Couldn't re-key — don't leave history and pixels disagreeing.
    pendingHistoryTargetRef.current = null;
    setPlacementMsg("Couldn't re-apply the background removal — run “Remove background (AI)” again.");
  };
  const canUndo = histCanUndo(history);
  const canRedo = histCanRedo(history);
  const canRevert = history.index > 0;
  /** Step the history with `move`, then apply — but REFUSE (and say so) when the target crosses a
   *  removal boundary we cannot honour, rather than stepping the index and desyncing the canvas. */
  const stepHistory = (move: () => void, label: string) => {
    if (locked) return;
    const before = useHistory.getState().index;
    move();
    const state = useHistory.getState();
    if (state.index === before) return;
    const target = currentSnapshot(state);
    if (!canApplySnapshot(target)) {
      useHistory.setState({ index: before }); // put the cursor back — nothing was applied
      setPlacementMsg("Can't step past the background removal — its matte isn't available any more.");
      logger?.log("warn", `Editor · ${label} refused — no matte available to re-apply the removal`);
      return;
    }
    applySnapshot(target);
    logger?.log("info", `Editor · ${label}`);
  };
  const undoGeo = () => {
    if (locked || !histCanUndo(useHistory.getState())) return;
    stepHistory(() => useHistory.getState().undo(), "Undo masking change");
  };
  const redoGeo = () => {
    if (locked || !histCanRedo(useHistory.getState())) return;
    stepHistory(() => useHistory.getState().redo(), "Redo masking change");
  };
  const revertGeo = () => {
    if (locked) return;
    stepHistory(
      () => useHistory.getState().reset(),
      "Revert masking to original (all auto edits undone)",
    );
  };

  return {
    /** THIS editor's history store (MN-5). Bound hook + `getState()`/`setState()`. */
    useHistory,
    history,
    snapshot,
    canUndo,
    canRedo,
    canRevert,
    undoGeo,
    redoGeo,
    revertGeo,
    canApplySnapshot,
    applySnapshot,
    pendingHistoryTargetRef,
    restoreRemovalRef,
    undoneMatteRef,
    undoneMatteKindRef,
    resetBackgroundRef,
    applyMatteToSourceRef,
  };
}
